'use client';

import { fetchMarketingSceneProductList } from '@/api/marketingPages';
import Image from '@/components/Image';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import type { Store } from '@/store';
import logger from '@/utils/client-logger';
import { useLocale } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { MarketingSceneProductsBlockConfig } from '../../../types';
import { MarketingRankingProductCard } from './MarketingRankingProductCard';
import {
  getMarketingRankingProductCardView,
  getStringValue,
  isMarketingFlashSaleSoldOutProduct,
  type MarketingProductItem,
  withS3Url,
} from './marketingAnniversaryUtils';
import { MarketingSwiperArrowButton } from './MarketingSwiperArrowButton';

const MARKETING_SCENE_PRODUCTS_PC_SPACE_BETWEEN = 16;
const MARKETING_SCENE_PRODUCTS_PC_SWIPER_SLIDE_STYLE = {
  width: '316px',
};
const MARKETING_SCENE_PRODUCTS_PC_PAGE_SIZE = 20;
const MARKETING_SCENE_PRODUCTS_WAP_PAGE_SIZE = 20;
const MARKETING_SCENE_PRODUCTS_WAP_DISPLAY_COUNT = 6;
const MARKETING_SCENE_PRODUCTS_US_LOCALE = 'en';

interface MarketingSceneProductsSectionProps {
  sceneProductsBlock: MarketingSceneProductsBlockConfig;
  marketingPageId: number;
  isPreview: boolean;
  adp?: string;
  isWap: boolean;
  onProductExposure?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onProductClick?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onTabClick?: (tabName: string, index: number) => void;
}

interface MarketingSceneProductCardProps {
  product: MarketingProductItem;
  index: number;
  tabName: string;
  isWap: boolean;
  currency: Store['currency'];
  userInfo: Store['userInfo'];
  onProductExposure?: (product: MarketingProductItem, index: number, tabName: string) => void;
  onProductClick?: (product: MarketingProductItem, index: number, tabName: string) => void;
}

function MarketingSceneProductCard({
  product,
  index,
  tabName,
  isWap,
  currency,
  userInfo,
  onProductExposure,
  onProductClick,
}: MarketingSceneProductCardProps) {
  const { ref: exposureRef, inView } = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });
  const exposureSentRef = useRef(false);
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const locale = useLocale();
  const cardView = getMarketingRankingProductCardView(product, currency, userInfo);
  const isSoldOut = isMarketingFlashSaleSoldOutProduct(product);
  const shouldStackWapPrice = isWap && locale.toLowerCase() !== MARKETING_SCENE_PRODUCTS_US_LOCALE;

  useEffect(() => {
    if (!inView || exposureSentRef.current) {
      return;
    }

    exposureSentRef.current = true;
    onProductExposure?.(product, index, tabName);
  }, [inView, index, onProductExposure, product, tabName]);

  return (
    <div ref={exposureRef} className="min-w-0">
      <MarketingRankingProductCard
        {...cardView}
        rank={index + 1}
        rankText=""
        soldText=""
        isLarge={false}
        isWap={isWap}
        showRankingMeta={false}
        contentClassName={
          isWap
            ? 'box-border flex min-h-0 flex-1 flex-col justify-end px-[8px] pb-[8px] text-center'
            : undefined
        }
        priceRowClassName={
          isWap
            ? shouldStackWapPrice
              ? 'mt-[6px] flex flex-col items-center gap-y-[2px] font-br'
              : 'mt-[6px] flex flex-wrap items-baseline justify-center gap-x-[6px] gap-y-[2px] font-br'
            : undefined
        }
        priceTextClassName={
          isWap ? 'shrink-0 font-bb text-[14px] font-bold leading-[17px] text-[#282828]' : undefined
        }
        originalPriceTextClassName={
          isWap
            ? shouldStackWapPrice
              ? 'shrink-0 whitespace-nowrap text-[12px] font-normal leading-[14px] text-[#808080] line-through'
              : 'truncate text-[12px] font-normal leading-[14px] text-[#808080] line-through'
            : undefined
        }
        isSoldOut={isSoldOut}
        soldOutText={tMarketing('flashSale.soldOut')}
        minWidth={isWap ? undefined : 316}
        height={isWap ? 'auto' : 304}
        onClick={() => onProductClick?.(product, index, tabName)}
      />
    </div>
  );
}

export function MarketingSceneProductsSection({
  sceneProductsBlock,
  marketingPageId,
  isPreview,
  adp,
  isWap,
  onProductExposure,
  onProductClick,
  onTabClick,
}: MarketingSceneProductsSectionProps) {
  const tMarketing = useTranslations('activity.campaigns.marketing');
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const swiperRef = useRef<SwiperType | null>(null);
  const loadingSceneIndexesRef = useRef<Set<number>>(new Set());
  const defaultSceneIndex = useMemo(() => {
    const configuredIndex = Number(sceneProductsBlock.default_scene_index);
    return Number.isFinite(configuredIndex) &&
      configuredIndex >= 0 &&
      configuredIndex < sceneProductsBlock.scenes.length
      ? configuredIndex
      : 0;
  }, [sceneProductsBlock.default_scene_index, sceneProductsBlock.scenes.length]);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(defaultSceneIndex);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingSceneIndex, setLoadingSceneIndex] = useState<number | null>(null);
  const [sceneProductLoaded, setSceneProductLoaded] = useState(() =>
    sceneProductsBlock.scenes.map((scene) => Boolean(scene.products_loaded)),
  );
  const [localSceneProducts, setLocalSceneProducts] = useState(() =>
    sceneProductsBlock.scenes.map((scene) => scene.products),
  );
  const fallbackProducts = sceneProductsBlock.products;
  const scenes = useMemo(
    () =>
      sceneProductsBlock.scenes
        .map((scene, index) => ({
          ...scene,
          source_index: index,
          products: localSceneProducts[index] || scene.products,
          products_loaded: sceneProductLoaded[index] ?? Boolean(scene.products_loaded),
          title: getStringValue(scene.title),
        }))
        .filter((scene) => !scene.products_loaded || scene.products.length > 0),
    [localSceneProducts, sceneProductLoaded, sceneProductsBlock.scenes],
  );
  const activeScene =
    scenes.find((scene) => scene.source_index === selectedSceneIndex) || scenes[0];
  const allProducts = useMemo(() => {
    const activeSceneProducts = activeScene?.products as Array<Record<string, unknown>> | undefined;
    const productList =
      activeSceneProducts?.length || activeScene?.product_template
        ? activeSceneProducts || []
        : fallbackProducts;

    return (productList || []).map((product) => product as MarketingProductItem);
  }, [activeScene?.product_template, activeScene?.products, fallbackProducts]);
  const displayCount = isWap ? MARKETING_SCENE_PRODUCTS_WAP_DISPLAY_COUNT : allProducts.length;
  const hasMoreProducts = allProducts.length > displayCount;
  const products = useMemo(
    () => (isExpanded || !hasMoreProducts ? allProducts : allProducts.slice(0, displayCount)),
    [allProducts, displayCount, hasMoreProducts, isExpanded],
  );
  const sceneBackgroundColor = isWap
    ? sceneProductsBlock.wap_background_color || sceneProductsBlock.background_color || '#282828'
    : sceneProductsBlock.pc_background_color || sceneProductsBlock.background_color || '#FFFFFF';
  const sceneProductsBackgroundColor = isWap ? sceneBackgroundColor : '#F4F4F4';

  useEffect(() => {
    setSelectedSceneIndex((current) =>
      scenes.some((scene) => scene.source_index === current)
        ? current
        : (scenes[0]?.source_index ?? defaultSceneIndex),
    );
  }, [defaultSceneIndex, scenes]);

  useEffect(() => {
    setSelectedSceneIndex(defaultSceneIndex);
    setSceneProductLoaded(sceneProductsBlock.scenes.map((scene) => Boolean(scene.products_loaded)));
    setLocalSceneProducts(sceneProductsBlock.scenes.map((scene) => scene.products));
  }, [defaultSceneIndex, sceneProductsBlock.scenes]);

  useEffect(() => {
    swiperRef.current?.slideTo(0);
  }, [selectedSceneIndex, products.length]);

  useEffect(() => {
    setIsExpanded(false);
  }, [sceneProductsBlock.display_count, sceneProductsBlock.products, selectedSceneIndex]);

  const syncSwiperEdgeState = useCallback((swiper: SwiperType) => {
    setIsBeginning((current) => (current === swiper.isBeginning ? current : swiper.isBeginning));
    setIsEnd((current) => (current === swiper.isEnd ? current : swiper.isEnd));
  }, []);

  const loadSceneProducts = useCallback(
    async (scene: (typeof scenes)[number], index: number) => {
      if (
        !scene ||
        !marketingPageId ||
        sceneProductLoaded[index] ||
        loadingSceneIndex === index ||
        loadingSceneIndexesRef.current.has(index)
      ) {
        return;
      }

      loadingSceneIndexesRef.current.add(index);
      setLoadingSceneIndex(index);

      try {
        const response = await fetchMarketingSceneProductList({
          marketingPageId,
          isPreview,
          itemIndex: scene.item_index ?? index,
          page: 1,
          pageSize: isWap
            ? MARKETING_SCENE_PRODUCTS_WAP_PAGE_SIZE
            : MARKETING_SCENE_PRODUCTS_PC_PAGE_SIZE,
          adp,
        });
        const items = response?.items || [];

        setLocalSceneProducts((current) =>
          current.map((products, currentIndex) => (currentIndex === index ? items : products)),
        );
        setSceneProductLoaded((current) =>
          current.map((value, currentIndex) => (currentIndex === index ? true : value)),
        );
      } catch (error) {
        logger.error(error, 'Failed to fetch marketing scene products:');
      } finally {
        loadingSceneIndexesRef.current.delete(index);
        setLoadingSceneIndex(null);
      }
    },
    [adp, isPreview, isWap, loadingSceneIndex, marketingPageId, sceneProductLoaded],
  );

  const handleSceneSelect = useCallback(
    (scene: (typeof scenes)[number], sourceIndex: number, trackingIndex: number) => {
      setSelectedSceneIndex(sourceIndex);
      setIsExpanded(false);
      onTabClick?.(scene.title, trackingIndex);
      void loadSceneProducts(scene, sourceIndex);
    },
    [loadSceneProducts, onTabClick],
  );

  useEffect(() => {
    if (!activeScene || sceneProductLoaded[activeScene.source_index]) {
      return;
    }

    void loadSceneProducts(activeScene, activeScene.source_index);
  }, [activeScene, loadSceneProducts, sceneProductLoaded]);

  const handleSceneViewMoreClick = useCallback(() => {
    setIsExpanded(true);
  }, []);

  if (!sceneProductsBlock.visible || scenes.length === 0) {
    return null;
  }

  const renderCategoryCard = (scene: (typeof scenes)[number], index: number) => {
    const sourceIndex = scene.source_index;
    const isActive = sourceIndex === selectedSceneIndex;
    const image = withS3Url(
      isWap
        ? getStringValue(scene.wap_image) ||
            getStringValue(scene.pc_image) ||
            getStringValue(scene.image)
        : getStringValue(scene.pc_image) ||
            getStringValue(scene.wap_image) ||
            getStringValue(scene.image),
    );
    const cardClassName = isWap
      ? `group relative shrink-0 cursor-pointer overflow-hidden rounded-[6px] border-0 bg-transparent p-0 transition-[width,height,margin,transform] duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-purple ${
          isActive ? 'h-[72px] w-[120px]' : 'mt-[7px] h-[58px] w-[98px]'
        }`
      : `group relative cursor-pointer overflow-hidden rounded-[12px] border-0 bg-transparent p-0 transition-transform duration-300 ease-out hover:scale-[1.015] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-purple ${
          isActive
            ? 'h-[220px] w-[367px] flex-none basis-[367px]'
            : 'aspect-[254/152] max-h-[152px] max-w-[254px] min-w-[120px] flex-1 basis-0'
        }`;

    return (
      <button
        key={`${scene.title}-${sourceIndex}`}
        type="button"
        className={cardClassName}
        aria-pressed={isActive}
        onClick={() => handleSceneSelect(scene, sourceIndex, index)}
      >
        {image ? (
          <Image
            src={image}
            alt={getStringValue(scene.alt) || scene.title}
            fill
            sizes={isWap ? '120px' : isActive ? '367px' : '20vw'}
            className={`object-cover transition-transform duration-300 ease-out ${
              isActive ? 'scale-100' : 'scale-[1.03] group-hover:scale-[1.06]'
            }`}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{
              background:
                index % 3 === 0
                  ? 'linear-gradient(135deg, #4b2f20 0%, #d6b88a 100%)'
                  : index % 3 === 1
                    ? 'linear-gradient(135deg, #2c7782 0%, #d7c6a8 100%)'
                    : 'linear-gradient(135deg, #475739 0%, #c8c5b5 100%)',
            }}
          />
        )}
        <span className="absolute inset-0 bg-black/20" />
        <span
          className={
            isWap
              ? `absolute inset-x-[8px] ${isActive ? 'bottom-[10px]' : 'bottom-[6px]'} truncate text-center font-bb text-[12px] font-bold leading-[14px] text-white`
              : `absolute inset-x-[8px] ${isActive ? 'bottom-[14px]' : 'bottom-[12px]'} truncate text-center text-white ${
                  isActive
                    ? 'font-bb text-[16px] font-bold leading-[19px]'
                    : 'font-br text-[14px] font-normal leading-[17px]'
                }`
          }
        >
          {scene.title}
        </span>
      </button>
    );
  };

  const renderProductCard = (product: MarketingProductItem, index: number) => {
    const tabName = activeScene?.title || '';

    return (
      <MarketingSceneProductCard
        key={`${getStringValue(product.shop_sku) || getStringValue(product.id) || index}`}
        product={product}
        index={index}
        tabName={tabName}
        isWap={isWap}
        currency={currency}
        userInfo={userInfo}
        onProductExposure={onProductExposure}
        onProductClick={onProductClick}
      />
    );
  };
  const sceneViewMoreButtonText = tMarketing('common.viewMore');
  const sectionTitle = sceneProductsBlock.title || '';
  const sectionSubTitle = sceneProductsBlock.sub_title || '';
  const sceneViewMoreButton = (
    <button
      type="button"
      className="mx-auto mt-[18px] flex h-[38px] w-[264px] max-w-full cursor-pointer items-center justify-center rounded-[40px] border border-white bg-transparent font-bb text-[14px] font-bold leading-[18px] text-white disabled:cursor-default disabled:opacity-60"
      onClick={handleSceneViewMoreClick}
    >
      {sceneViewMoreButtonText}
    </button>
  );

  return (
    <section
      className={isWap ? 'relative z-10 mx-[8px] my-[20px]' : 'relative z-10 my-[20px] w-full'}
      style={{ backgroundColor: sceneBackgroundColor }}
    >
      <div
        className={
          isWap
            ? 'pb-[20px] pt-[20px]'
            : 'mx-auto w-[calc(100%_-_96px)] max-w-[1680px] py-[40px] min-[1281px]:w-[calc(100%_-_240px)]'
        }
      >
        <div className={isWap ? 'mb-[16px] text-center' : 'mb-[24px] text-center'}>
          <h2
            className={`font-bb font-bold ${isWap ? 'text-white' : 'text-[#282828]'} ${
              isWap ? 'text-[20px] leading-[24px]' : 'text-[32px] leading-[38px]'
            }`}
          >
            {sectionTitle}
          </h2>
          {sectionSubTitle && (
            <p
              className={`mt-[4px] font-br font-normal ${isWap ? 'text-white opacity-60' : 'text-[#808080]'} ${
                isWap ? 'text-[12px] leading-[14px]' : 'text-[14px] leading-[17px]'
              }`}
            >
              {sectionSubTitle}
            </p>
          )}
        </div>

        <div
          className={
            isWap
              ? 'scrollbar-hide flex h-[72px] items-start gap-[6px] overflow-x-auto overscroll-x-contain [-webkit-overflow-scrolling:touch]'
              : 'flex h-[220px] w-full items-center gap-[8px] overflow-hidden'
          }
        >
          {scenes.map(renderCategoryCard)}
        </div>

        {isWap ? (
          <>
            <div
              className="mt-[12px] grid gap-[6px]"
              style={{
                gridTemplateColumns: 'repeat(2, minmax(min(176px, calc((100% - 6px) / 2)), 1fr))',
              }}
            >
              {products.map((product, index) => renderProductCard(product, index))}
            </div>
            {hasMoreProducts && !isExpanded && sceneViewMoreButton}
          </>
        ) : (
          <div
            className="relative mt-[8px] min-h-[352px] overflow-hidden rounded-[12px] px-[20px] py-[24px]"
            style={{ backgroundColor: sceneProductsBackgroundColor }}
          >
            <div className="group relative">
              <Swiper
                modules={[Navigation]}
                slidesPerView="auto"
                slidesPerGroup={1}
                spaceBetween={MARKETING_SCENE_PRODUCTS_PC_SPACE_BETWEEN}
                navigation={{
                  nextEl: '.marketing-scene-products-next',
                  prevEl: '.marketing-scene-products-prev',
                }}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  syncSwiperEdgeState(swiper);
                }}
                onSlideChange={syncSwiperEdgeState}
                onProgress={syncSwiperEdgeState}
                onReachBeginning={syncSwiperEdgeState}
                onReachEnd={syncSwiperEdgeState}
                onFromEdge={syncSwiperEdgeState}
                onTransitionEnd={syncSwiperEdgeState}
                className="!overflow-visible"
              >
                {products.map((product, index) => (
                  <SwiperSlide
                    key={`${getStringValue(product.id) || getStringValue(product.shop_sku) || index}`}
                    style={MARKETING_SCENE_PRODUCTS_PC_SWIPER_SLIDE_STYLE}
                  >
                    {renderProductCard(product, index)}
                  </SwiperSlide>
                ))}
              </Swiper>
              <MarketingSwiperArrowButton
                className="marketing-scene-products-prev left-[8px]"
                direction="prev"
                hidden={isBeginning}
                label="Previous"
                onClick={() => swiperRef.current?.slidePrev()}
              />
              <MarketingSwiperArrowButton
                className="marketing-scene-products-next right-[8px]"
                direction="next"
                hidden={isEnd}
                label="Next"
                onClick={() => swiperRef.current?.slideNext()}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
